import { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { Menu, Moon, Sun, X } from 'lucide-react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { useReducedMotion } from '@/lib/useReducedMotion'
import { useScroll } from '@/components/ui/use-scroll'

const portfolioLinks = [
  { label: 'Work', href: '/work' },
  { label: 'About', href: '/about' },
  { label: 'Writing', href: '/blog' },
  { label: 'Contact', href: '/#connect' },
]

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [lightMode, setLightMode] = useState(() => document.documentElement.dataset.theme === 'light')
  const menuRef = useRef<HTMLDivElement>(null)
  const toggleRef = useRef<HTMLButtonElement>(null)
  const location = useLocation()
  const reducedMotion = useReducedMotion()
  const scrolled = useScroll(10)

  const toggleTheme = () => {
    const theme = lightMode ? 'dark' : 'light'
    document.documentElement.dataset.theme = theme
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', theme === 'light' ? '#f7f7f2' : '#0b0d0b')
    setLightMode(!lightMode)
    try { localStorage.setItem('portfolio-theme', theme) } catch { /* Theme still works without storage. */ }
  }

  useEffect(() => { setMobileMenuOpen(false) }, [location.pathname, location.hash])

  useEffect(() => {
    if (!mobileMenuOpen) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const menu = menuRef.current!
    const toggle = toggleRef.current
    const controls = Array.from(menu.querySelectorAll<HTMLElement>('a, button'))
    controls[0].focus()
    const desktop = window.matchMedia('(min-width: 768px)')
    const closeOnDesktop = () => { if (desktop.matches) setMobileMenuOpen(false) }
    desktop.addEventListener('change', closeOnDesktop)
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileMenuOpen(false)
      if (event.key === 'Tab') {
        const first = controls[0]
        const last = controls[controls.length - 1]
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault()
          last.focus()
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault()
          first.focus()
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', handleKeyDown)
      desktop.removeEventListener('change', closeOnDesktop)
      toggle?.focus({ preventScroll: true })
    }
  }, [mobileMenuOpen])

  return (
    <>
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <header className="portfolio-header fixed inset-x-0 top-0 z-50" data-scrolled={scrolled}>
      <nav aria-label="Main navigation">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 lg:h-20 lg:px-8">
          <Link to="/" className="font-mono text-xl text-text-primary">Will <span className="text-accent-primary">Tran</span></Link>
          <div className="flex items-center gap-3">
          <div className="hidden items-center gap-2 md:flex">
            {portfolioLinks.map((link) => link.label === 'Contact' ? (
              <Link key={link.label} to={link.href} className="px-4 py-2 font-mono text-sm text-text-secondary hover:text-accent-primary">{link.label}</Link>
            ) : (
              <NavLink key={link.label} to={link.href} className={({ isActive }) => `px-4 py-2 font-mono text-sm hover:text-accent-primary ${isActive ? 'text-accent-primary' : 'text-text-secondary'}`}>{link.label}</NavLink>
            ))}
            <Button variant="outline" href="https://github.com/kv0906" className="ml-4">GitHub</Button>
          </div>
          <button
            type="button"
            role="switch"
            aria-label="Light mode"
            aria-checked={lightMode}
            title={lightMode ? 'Switch to dark mode' : 'Switch to light mode'}
            onClick={toggleTheme}
            className="relative flex h-11 items-center gap-1 rounded-full border border-vibe-border-strong bg-vibe-elevated p-1 text-text-secondary hover:border-accent-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-accent-primary"
          >
            <motion.span aria-hidden="true" className="absolute left-1 top-[5px] h-8 w-8 rounded-full bg-card-bg shadow-sm" initial={false} animate={{ x: lightMode ? 0 : 36 }} transition={reducedMotion ? { duration: 0 } : { type: 'spring', stiffness: 500, damping: 35 }} />
            <span className={`relative flex h-8 w-8 items-center justify-center rounded-full ${lightMode ? 'text-accent-primary' : ''}`}><Sun size={16} aria-hidden="true" /></span>
            <span className={`relative flex h-8 w-8 items-center justify-center rounded-full ${!lightMode ? 'text-accent-primary' : ''}`}><Moon size={16} aria-hidden="true" /></span>
          </button>
          <button ref={toggleRef} className="flex h-11 w-11 items-center justify-center rounded-md border border-vibe-border text-text-secondary hover:text-accent-primary md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'} aria-expanded={mobileMenuOpen} aria-controls="mobile-menu">
            {mobileMenuOpen ? <X size={20} aria-hidden="true" /> : <Menu size={20} aria-hidden="true" />}
          </button>
          </div>
        </div>
      </nav>
      </header>
      {mobileMenuOpen && (
        <div ref={menuRef} id="mobile-menu" className="mobile-navigation fixed inset-0 z-[60] overflow-y-auto p-6 md:hidden" role="dialog" aria-modal="true" aria-label="Navigation menu">
          <button onClick={() => setMobileMenuOpen(false)} className="ml-auto flex items-center gap-2 rounded-md border border-vibe-border p-3 text-text-secondary hover:text-accent-primary"><X size={20} aria-hidden="true" />Close menu</button>
          <motion.nav initial={reducedMotion ? false : { opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: reducedMotion ? 0 : 0.16, ease: 'easeOut' }} aria-label="Mobile navigation" className="mt-8">
            {portfolioLinks.map((link) => (
              <Link key={link.label} to={link.href} onClick={() => setMobileMenuOpen(false)} className="block border-b border-vibe-border py-5 font-mono text-xl text-text-primary hover:text-accent-primary">{link.label}</Link>
            ))}
            <div className="mt-8"><Button variant="outline" href="https://github.com/kv0906">GitHub</Button></div>
          </motion.nav>
        </div>
      )}
    </>
  )
}
